import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';
import type { ProfileResponse } from '../../lib/api/profile-api-client';

/**
 * Profile slice state + actions (round-22 Phase D — realigned to
 * the shipped Smithy contract).
 *
 * Per design §3.1 (Slices layer) the profile slice carries the
 * authenticated user's profile record + independent fetch and
 * upsert lifecycles for the two `ProfileApi` v1 operations
 * described in the Smithy IDL (`GetProfileV1` and
 * `UpsertProfileV1`).
 *
 *  - `fetchStatus` — lifecycle of `GetProfileV1`. The
 *    `'not-found'` terminal state is the first-time-visitor
 *    branch — the `<OAuthCallbackPage>` reads it to redirect to
 *    `/sign-up`.
 *  - `upsertStatus` — lifecycle of `UpsertProfileV1` (used by
 *    `<SignUpPage>`, `<ProfilePage>`, and the newsletter opt-in
 *    toggle).
 *
 * The pre-round-22 `emailConfirmStatus` lifecycle has been
 * deleted — email confirmation runs entirely through Cognito's
 * confirm-signup flow now (`<VerifyEmailPage>` + the ID token's
 * `email_verified` claim), so the SPA no longer holds a separate
 * confirm-email state.
 */

/** `GetProfileV1` lifecycle. */
export type ProfileFetchStatus =
  | 'idle'
  | 'loading'
  | 'succeeded'
  | 'failed'
  | 'not-found';

/** `UpsertProfileV1` lifecycle. */
export type ProfileUpsertStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

/** Profile slice state shape. */
export interface ProfileState {
  readonly fetchStatus: ProfileFetchStatus;
  readonly upsertStatus: ProfileUpsertStatus;
  readonly profile?: ProfileResponse;
  readonly fetchError?: string;
  readonly upsertError?: string;
}

const initialState: ProfileState = {
  fetchStatus: 'idle',
  upsertStatus: 'idle',
};

export const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    profileFetchRequested(state) {
      state.fetchStatus = 'loading';
      state.fetchError = undefined;
    },
    profileFetchSucceeded(
      state,
      action: PayloadAction<{ profile: ProfileResponse }>,
    ) {
      state.fetchStatus = 'succeeded';
      state.profile = castDraft(action.payload.profile);
      state.fetchError = undefined;
    },
    profileFetchNotFound(state) {
      state.fetchStatus = 'not-found';
      state.profile = undefined;
      state.fetchError = undefined;
    },
    profileFetchFailed(state, action: PayloadAction<{ error: string }>) {
      state.fetchStatus = 'failed';
      state.fetchError = action.payload.error;
    },
    profileUpsertRequested(state) {
      state.upsertStatus = 'loading';
      state.upsertError = undefined;
    },
    profileUpsertSucceeded(
      state,
      action: PayloadAction<{ profile: ProfileResponse }>,
    ) {
      state.upsertStatus = 'succeeded';
      state.profile = castDraft(action.payload.profile);
      state.upsertError = undefined;
      // A successful upsert proves the profile exists — flip
      // fetchStatus from any prior `'not-found'` to `'succeeded'`
      // so the SPA's guards let the user past `/sign-up`.
      state.fetchStatus = 'succeeded';
      state.fetchError = undefined;
    },
    profileUpsertFailed(state, action: PayloadAction<{ error: string }>) {
      state.upsertStatus = 'failed';
      state.upsertError = action.payload.error;
    },
  },
});

export const {
  profileFetchRequested,
  profileFetchSucceeded,
  profileFetchNotFound,
  profileFetchFailed,
  profileUpsertRequested,
  profileUpsertSucceeded,
  profileUpsertFailed,
} = profileSlice.actions;

export default profileSlice.reducer;
